import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import logoCadrage from "../data/content/logo-cadrage.json";

const clampNumber = (value, fallback, min, max) => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.min(max, Math.max(min, number)) : fallback;
};

const logoZoom = clampNumber(logoCadrage.zoom, 250, 100, 500);
const logoPositionX = clampNumber(logoCadrage.positionX, 50, 0, 100);
const logoPositionY = clampNumber(logoCadrage.positionY, 60, 0, 100);

const navLinks = [
  { name: "Accueil", path: "/" },
  {
    name: "Mes réalisations",
    path: "/realisations",
    hasDropdown: true,
    subLinks: [
      { name: "Wedding Cake", path: "/realisations?cat=wedding" },
      { name: "Layer Cake", path: "/realisations?cat=layer" },
      { name: "Number Cake", path: "/realisations?cat=number" },
      { name: "Letter Cake", path: "/realisations?cat=letter" },
      { name: "Cupcakes", path: "/realisations?cat=cupcake" },
    ],
  },
  { name: "Tarifs", path: "/tarifs" },
  { name: "Commande", path: "/commande" },
  { name: "Avis clients", path: "/avis" },
  { name: "Contact", path: "/contact" },
];

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setDropdownOpen(false);
  }, [location]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? "bg-[#F6F7F1]/95 backdrop-blur-md shadow-lg py-2"
          : "bg-[#F6F7F1]/80 backdrop-blur-sm py-4"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div
              aria-hidden="true"
              className="w-12 h-12 shrink-0 rounded-full border-2 border-white bg-white shadow-md group-hover:shadow-lg transition-shadow duration-300"
              style={{
                backgroundImage: "url('/ma-commande-patisserie-ngoussi.png')",
                backgroundSize: `${logoZoom}% ${logoZoom}%`,
                backgroundPosition: `${logoPositionX}% ${logoPositionY}%`,
              }}
            />
            <div className="flex flex-col">
              <span className="font-playfair text-sm text-[#E5007D] font-medium leading-tight">
                Ma commande
              </span>
              <span className="font-playfair text-lg font-bold text-gray-900 leading-tight tracking-wide">
                PÂTISSERIE
              </span>
              <span className="font-playfair text-xs font-bold text-[#E5007D] tracking-[0.2em]">
                NGOUSSI
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <div
                key={link.name}
                className="relative"
                onMouseEnter={() => link.hasDropdown && setDropdownOpen(true)}
                onMouseLeave={() => link.hasDropdown && setDropdownOpen(false)}
              >
                <Link
                  to={link.path}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 flex items-center gap-1 ${
                    location.pathname === link.path
                      ? "text-[#E5007D] bg-pink-50"
                      : "text-gray-700 hover:text-[#E5007D] hover:bg-pink-50/50"
                  }`}
                >
                  {link.name}
                  {link.hasDropdown && (
                    <ChevronDown
                      className={`w-4 h-4 transition-transform duration-300 ${
                        dropdownOpen ? "rotate-180" : ""
                      }`}
                    />
                  )}
                </Link>

                {/* Dropdown */}
                {link.hasDropdown && (
                  <AnimatePresence>
                    {dropdownOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full left-0 mt-1 bg-white rounded-xl shadow-xl border border-gray-100 py-2 min-w-[200px] z-[60]"
                      >
                        {link.subLinks.map((sub) => (
                          <Link
                            key={sub.name}
                            to={sub.path}
                            className="block px-4 py-2.5 text-sm text-gray-700 hover:text-[#E5007D] hover:bg-pink-50 transition-colors duration-200"
                          >
                            {sub.name}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                )}
              </div>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-pink-50 transition-colors"
          >
            {mobileOpen ? (
              <X className="w-6 h-6 text-gray-700" />
            ) : (
              <Menu className="w-6 h-6 text-gray-700" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-[#F6F7F1] border-t border-gray-100 overflow-hidden"
          >
            <div className="px-4 py-4 space-y-1">
              {navLinks.map((link) => (
                <div key={link.name}>
                  <Link
                    to={link.path}
                    className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      location.pathname === link.path
                        ? "text-[#E5007D] bg-pink-50"
                        : "text-gray-700 hover:text-[#E5007D] hover:bg-pink-50/50"
                    }`}
                  >
                    {link.name}
                  </Link>
                  {link.hasDropdown && (
                    <div className="pl-6 space-y-1">
                      {link.subLinks.map((sub) => (
                        <Link
                          key={sub.name}
                          to={sub.path}
                          className="block px-4 py-2 text-sm text-gray-500 hover:text-[#E5007D] transition-colors"
                        >
                          {sub.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
